import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileWarning } from 'lucide-react';
import type { JobFileEntry, JobFileExpenseUsage, JobFileInUseReport } from '@jobos/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { jobFileInUseReports, jobFileLockedExpenses, useDeleteJobFile } from '@/hooks/useJobFiles';

// Deleting a library file.
//
// Three outcomes need saying, and only one of them is a plain error. A photo an
// inspection report is using, or one an expense is holding as its receipt, can't
// be deleted at all until that record lets go of it — so each refusal names what
// is holding it and points at where to go. "Delete failed" on its own would
// leave the user with nowhere to go.

interface JobFileDeleteDialogProps {
  jobId: string;
  /** The file to delete; null closes the dialog. */
  file: JobFileEntry | null;
  onClose: () => void;
}

export function JobFileDeleteDialog({ jobId, file, onClose }: JobFileDeleteDialogProps) {
  return (
    <Dialog open={file != null} onOpenChange={(open) => !open && onClose()}>
      {file && (
        <DialogContent className="max-w-lg">
          {/* Keyed on the file so a previous file's blocked state or error can't
              carry over into the next confirmation. */}
          <DeleteBody key={file.id} jobId={jobId} file={file} onClose={onClose} />
        </DialogContent>
      )}
    </Dialog>
  );
}

function DeleteBody({
  jobId,
  file,
  onClose,
}: {
  jobId: string;
  file: JobFileEntry;
  onClose: () => void;
}) {
  const { mutateAsync: deleteFile, isPending } = useDeleteJobFile(jobId);
  // Set once a 409 names them; from then on the dialog is a blocked state and
  // there is nothing left to confirm.
  const [blockedBy, setBlockedBy] = useState<JobFileInUseReport[] | null>(null);
  // Set once a 409 names them. Separate from the reports above: what is holding
  // the photo decides both the wording and where the user is sent.
  const [heldByExpenses, setHeldByExpenses] = useState<JobFileExpenseUsage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteFile(file.id);
      onClose();
    } catch (err) {
      const reports = jobFileInUseReports(err);
      if (reports) {
        setBlockedBy(reports);
        return;
      }
      const expenses = jobFileLockedExpenses(err);
      if (expenses) {
        setHeldByExpenses(expenses);
        return;
      }
      // The hook has already toasted this one. Repeating it inline keeps the
      // reason next to the button that produced it, and the dialog open means a
      // second attempt costs one click rather than finding the file again.
      setError(err instanceof Error ? err.message : 'Failed to delete file.');
    }
  };

  if (blockedBy) return <BlockedState jobId={jobId} reports={blockedBy} onClose={onClose} />;
  if (heldByExpenses)
    return <HeldByExpensesState jobId={jobId} expenses={heldByExpenses} onClose={onClose} />;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Delete file</DialogTitle>
        <DialogDescription>
          <span className="font-medium text-gray-900">{file.fileName}</span> will be removed from
          this job for everyone. This can't be undone.
        </DialogDescription>
      </DialogHeader>

      {error && (
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="button" variant="destructive" onClick={handleDelete} disabled={isPending}>
          {isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogFooter>
    </>
  );
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * The expenses using the photo as their receipt, and where to go about it.
 *
 * Listed rather than linked one by one: an expense has no page of its own, so
 * the link goes to the tab they all live on, and the store, amount and date are
 * what make one findable once there.
 */
function HeldByExpensesState({
  jobId,
  expenses,
  onClose,
}: {
  jobId: string;
  expenses: JobFileExpenseUsage[];
  onClose: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FileWarning className="h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
          Still in use
        </DialogTitle>
        <DialogDescription>
          {expenses.length === 1
            ? 'This photo is the receipt on an expense. Delete that expense, or give it a different receipt, then delete the photo here.'
            : `This photo is the receipt on ${expenses.length} expenses. Delete them, or give each a different receipt, then delete the photo here.`}
        </DialogDescription>
      </DialogHeader>

      <ul className="space-y-1 py-2">
        {expenses.map((expense) => (
          <li key={expense.id} className="text-sm text-gray-700">
            <span className="font-medium">{expense.storeName}</span>
            {' — '}
            {money.format(expense.amount)}
            {', '}
            {expense.expenseDate}
          </li>
        ))}
      </ul>

      <DialogFooter className="sm:justify-between">
        <Link
          to={`/jobs/${jobId}?tab=expenses`}
          onClick={onClose}
          className="self-center text-sm font-medium text-blue-600 hover:underline"
        >
          Go to Expenses
        </Link>
        <Button type="button" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * What is holding the photo, and where to go about it.
 *
 * No retry button: nothing changes until the photo is off those reports, so the
 * only useful action is following one of the links.
 */
function BlockedState({
  jobId,
  reports,
  onClose,
}: {
  jobId: string;
  reports: JobFileInUseReport[];
  onClose: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FileWarning className="h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
          Still in use
        </DialogTitle>
        <DialogDescription>
          {reports.length === 1
            ? 'This photo is on an inspection report. Remove it from the report first, then delete it here.'
            : `This photo is on ${reports.length} inspection reports. Remove it from each one first, then delete it here.`}
        </DialogDescription>
      </DialogHeader>

      <ul className="space-y-1 py-2">
        {reports.map((report) => (
          <li key={report.id}>
            <Link
              to={`/jobs/${jobId}/inspection-reports/${report.id}`}
              onClick={onClose}
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              {report.title}
            </Link>
          </li>
        ))}
      </ul>

      <DialogFooter>
        <Button type="button" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  );
}
